// Side-effect import: it augments `Palette` with the `DataGrid` slot MUI X reads
// its own CSS variables from. See `DataGrid` at the bottom of `buildPalette`.
import '@mui/x-data-grid/themeAugmentation';

import { alpha, type PaletteMode, type PaletteOptions } from '@mui/material/styles';

/**
 * Northstar palette.
 *
 * Companion to `typography.ts`, with one structural difference: type is
 * mode-blind, colour is not, so the default export is a factory,
 * `buildPalette(mode)`, not an object.
 *
 * Every value the theme's component overrides need lives here, including the
 * `surface` tokens added by the module augmentation below. That is what lets
 * `index.ts` read colour exclusively from `theme.palette` instead of keeping
 * its own aliases.
 *
 * Contrast is a contract, not a hope: `palette.test.ts` asserts every pair with
 * an independent WCAG oracle (`contrast.ts`). Change a value here and that test
 * tells you whether it still clears AA.
 */

declare module '@mui/material/styles' {
  interface Palette {
    surface: Surface;
    dataviz: Dataviz;
    accent: Accents;
    softInk: SoftInk;
    spotlight: Spotlight;
    focusRing: string;
    category: CategoryInks;
    contactInk: ContactInks;
  }
  interface PaletteOptions {
    surface?: Surface;
    dataviz?: Dataviz;
    accent?: Accents;
    softInk?: SoftInk;
    spotlight?: Spotlight;
    focusRing?: string;
    category?: CategoryInks;
    contactInk?: ContactInks;
  }
}

/**
 * One colour per LP category of the HNWIs page (#553/#554): Exit entrepreneurs,
 * Business Angels, Family Offices, C-levels.
 *
 * A category is an identity, not a series: the same four hues mark the band's
 * figures AND the chip in every profile table's Types column, so a Family
 * Office reads blue wherever it appears. That is why they are not
 * `dataviz.categorical`, whose order means nothing outside the chart holding it.
 *
 * Every value is an INK: it writes the selected figure's label and the chip's
 * text, so it has to clear 4.5:1 on the three surfaces text lands on, in both
 * modes (asserted in `palette.test.ts`). Dark takes the Pencil board's hues as
 * drawn; light walks each one down its own hue until it holds on white.
 */
export interface CategoryInks {
  exit: string;
  ba: string;
  fo: string;
  clevel: string;
}

/**
 * The colour code of a contact (#704): green when someone verified it, red when
 * nobody has. The email and phone icons of the HNWIs Contact column and the dot
 * before a contact on the profile wear it.
 *
 * Its own token, not `success` / `error`: `success.main` sits at 164°, 14° from
 * the brand teal, and in dark mode a "verified" icon read as one more cyan
 * accent. The green takes the 105° anchor of `dataviz.categorical`, the one
 * placed to stay clear of the teal; the red is `error` (its light value walked
 * down to `softInk.error`, since `error.main` manages 2.92:1 on white).
 *
 * Each is an icon colour, so it clears 3:1 on the card (WCAG 1.4.11, asserted in
 * `palette.test.ts`).
 */
export interface ContactInks {
  verified: string;
  unverified: string;
}

/**
 * The one saturated block on a page, and there is only ever one.
 *
 * Everything else in this file paints a surface the content sits *on*. This
 * paints a surface that is meant to be *seen*: the single figure a screen is
 * organised around, in a filled card with its own ink. The company Overview
 * uses it for data freshness, the one number there that decides how much of the
 * rest can be trusted.
 *
 * Filled, so its ink cannot come from `text.*`; those are measured against the
 * canvas and the card, not against this. Hence `ink` and `muted` ship with the
 * fill, exactly as `surface` ships a border with its background.
 *
 * A second spotlight on the same screen is not a second token, it is a design
 * error: two things shouting is the same as neither.
 */
interface Spotlight {
  /** The filled card. */
  bg: string;
  /** The figure. */
  ink: string;
  /** Its label and caption: quieter, still AA on `bg`. */
  muted: string;
}

interface Dataviz {
  /**
   * Categorical series colours, in order. Index it, never hand a chart a hex.
   *
   * Charts were the last place still holding private colour lists (four of
   * them, two identical). They are also the one place where a colour IS the
   * data: a slice nobody can see is not a style bug, it is a missing number.
   *
   * Five, and there is no sixth. Past five the answer is never another hue;
   * it is `neutral`, below.
   */
  categorical: string[];
  /**
   * The "Other" bucket. Deliberately not a member of `categorical`: it is not a
   * category, it is what happens to the tail. Typing it apart is what stops the
   * next chart from reaching for `categorical[5]`.
   */
  neutral: string;
}

/**
 * The shell's accent: what marks the current row, per zone.
 *
 * One shape for both zones, because they are the same idea twice: teal in the
 * client, amber in /admin. Written as a pair rather than as an admin special
 * case, it deletes the `isAdminMode ? ... : ...` from every place the nav paints
 * itself, which is the difference between a system with a variant and the two
 * parallel ones this shell used to run.
 *
 * `main` is the accent used as INK, on `bg`, which is the accent itself at low
 * opacity. That pairing is the whole difficulty: a hue on a tint of itself is
 * the narrowest contrast in the palette, and it is where both zones had already
 * failed. Amber is also the brand gold (`warning.main`); admin is not a
 * different product, it is a context to be careful in.
 */
interface Accent {
  /** Text, icons, and the lit switch. */
  main: string;
  /** Its tint: the active row, the chips. */
  bg: string;
  /** The tint one step up, for hovering the active row. */
  hover: string;
}

type Accents = Record<'client' | 'admin', Accent>;

/**
 * A surface is a fill AND its boundary, so the two ship together.
 *
 * Which half carries depends on the mode, and on how big the thing is. The
 * dark fill step is 1.11:1 (`default` → `card`) and that reads perfectly well
 * across a whole card: over a large field the eye resolves a few percent of
 * luminance. The same ratio in a 1px line reads as nothing, which is why the
 * old 0.06 border (1.08:1) was decoration while the fill behind it did the
 * work. A contrast ratio models a thin line, not an area; do not read one as
 * the other, as an earlier version of this file did.
 *
 * So `cardBorder` is not a rescue: the card was always visible. It buys an
 * edge instead of a soft fade. In light mode the border cannot buy even that
 * (white cannot climb above white), so the shadow carries and it stays a
 * hairline.
 */
interface Surface {
  card: string;
  /** Gives the card a defined edge. Only resolves as a line in dark mode. */
  cardBorder: string;
  elevated: string;
  elevatedBorder: string;
  /** The decorative wash behind the chrome. See `AMBIENT`. */
  ambient: Ambient;
  /**
   * The chrome's edge, and in dark mode, the only thing separating it from the
   * canvas at all.
   *
   * The dark chrome sits 1.02:1 off the canvas: as fills they are the same
   * colour. Lifting it until the fill separated was tried and cost legibility
   * (secondary text fell from 6.22:1 to 5.91, and to 4.63 where a halo peaks),
   * for a step nobody would have called a surface anyway. So the line carries,
   * exactly as the shadow carries the card's edge in light mode. Same shape of
   * answer, opposite mode.
   */
  chromeBorder: string;
  /**
   * The chrome: TopBar and SideNav, and nothing else. Not `paper`: dialogs and
   * menus keep that, and they are not the chrome.
   *
   * Translucent by nature. Both bars are fixed with a `backdrop-filter`, so what
   * you see is this over whatever the page put behind it; measure it with
   * `flatten`, never on its own.
   *
   * Dark and light are not the same idea flipped, and they are not separated the
   * same way either. In light the chrome is a cooler, *darker* panel than the
   * content it frames (there is no room above white, so the difference points
   * down) and that fill is enough on its own. In dark there is no fill that
   * works: anything light enough to read as a step has walked into `card`, which
   * sits between. So dark separates with `chromeBorder` instead.
   */
  chrome: string;
}

/* ─── Brand ─────────────────────────────────────────────────────── */
export const INVYO_TEAL = '#00E0DA';
export const INVYO_MAGENTA = '#FF008C';
export const INVYO_GOLD = '#FFC200';

/**
 * The ink laid on every brand swatch, in BOTH modes.
 *
 * Mode-independence is the point: the swatches are identical in light and dark,
 * so their ink is too. Keying it on the mode is what put white on #00E0DA
 * (1.66:1) across the light-themed auth pages.
 *
 * It is also the only ink that clears AA on all six swatches; the design's
 * #06262A fails on magenta (4.27:1). Not a new colour: it is the dark
 * background promoted to a semantic token.
 */
export const INVYO_INK = '#080C14';

/* ─── Surfaces & content, per mode ──────────────────────────────── */

interface Surfaces {
  /** App background. */
  default: string;
  /** Chrome surface (top bar, side nav, dialogs). */
  paper: string;
  /** Content surface (cards, menus, tables). Distinct from `paper` on purpose. */
  card: string;
  /** Surface floating above content (tooltips). */
  elevated: string;
  /** Tinted grey the borders, dividers and hover states are derived from. */
  neutral: string;
  /**
   * The opaque colour the chrome's translucent surface composites from. In dark
   * that is `paper` itself; in light it is a cool slate, because white over
   * white is not a surface.
   */
  chrome: string;
  textPrimary: string;
  textSecondary: string;
  textDisabled: string;
}

const SURFACES: Record<PaletteMode, Surfaces> = {
  dark: {
    default: '#080C14',
    paper: '#0F1420',
    card: '#131926',
    elevated: '#1E2536',
    neutral: '#8494AB',
    chrome: '#0D1119',
    textPrimary: '#E8EDF5',
    textSecondary: '#8494AB',
    textDisabled: '#5F6D85',
  },
  light: {
    default: '#F4F6FA',
    paper: '#FFFFFF',
    card: '#FFFFFF',
    elevated: '#FFFFFF',
    neutral: '#94A3B8',
    chrome: '#E3EAF4',
    textPrimary: '#1A2035',
    // Darkened twice, each time because a new surface appeared under it. First
    // from #64748B, which reached only 4.40:1 on `default`. Then from #5D6B82,
    // which cleared the canvas at 4.99:1 but landed on 4.50:1 where the chrome
    // sits over a halo at full strength, passing by three thousandths, i.e. by
    // rounding. 4.93:1 there now, and still visibly the quieter of the two inks.
    textSecondary: '#586579',
    textDisabled: '#7C8A9E',
  },
};

/**
 * The categorical scale, per mode.
 *
 * A slice must clear 3:1 against the card it sits on: WCAG 1.4.11, and here it
 * is not pedantry: the donut separates its slices with a gap that shows the card
 * through, so each slice is read against the card and nothing else.
 *
 * The brand anchors clear that on a dark card by a mile (up to 10.85:1). On
 * white they collapse: teal 1.66:1, gold 1.62:1, green 1.89:1. Three of five
 * slices were invisible in light mode. Nothing is wrong with the anchors: they
 * are luminous by design, and white is luminous too.
 *
 * So light mixes every anchor 35% toward the ink. Every one, not only the three
 * that failed; a set where teal is muted and magenta stays vivid is no longer a
 * set. 35% is where the worst slice reaches 3.57:1; 30% only reaches 3.15:1,
 * which is close enough to the line that rounding decides it.
 *
 * The hue does not move: it carries the identity. Only the luminance does, and
 * it carries the legibility. Each does one job.
 *
 * The anchors sit at 46° / 105° / 178° / 268° / 327°, no two closer than 59°.
 * Three are the brand (gold, teal, magenta); green and violet are placed to fill
 * the gaps, not imported from a UI kit. 268° is the slot the admin violet used
 * to occupy; dropping it from the chrome is what freed it here.
 *
 * A first draft of this scale used #2DD4A8 for the green. It sat 14° from the
 * teal (two slices of the same colour on a donut) and it is `success.main`, so
 * a category would have been saying "passed". Contrast was fine; contrast was
 * never the question. `palette.test.ts` now holds the geometry too.
 */
const DATAVIZ_CATEGORICAL: Record<PaletteMode, string[]> = {
  //      teal 178°   magenta 327°  gold 46°    green 105°   violet 268°
  dark: ['#00E0DA', '#FF008C', '#FFC200', '#66E03E', '#A162E8'],
  light: ['#039695', '#A90462', '#A98207', '#45962F', '#6B449E'],
};

/** The tail bucket. Low saturation on purpose; it must not read as an anchor. */
const DATAVIZ_NEUTRAL: Record<PaletteMode, string> = {
  dark: '#8494AB',
  light: '#596476',
};

/**
 * The keyboard focus ring. Its own token, not `primary.main`.
 *
 * Not primary. A focus indicator is a system affordance, not brand expression,
 * and coupling it to the brand is how it ends up recoloured by whatever section
 * you are in. And not the brand teal in light mode,
 * where it manages 1.53:1 against the canvas: the one thing a focus ring cannot
 * be is invisible.
 *
 * The gap between ring and component is CSS `outline-offset`, not a colour, so
 * the ring never touches what it surrounds and never has to know what surface it
 * landed on; the transparent gap shows whatever is really behind it.
 */
/**
 * How much of the chrome is the chrome, and how much is what lies behind it.
 *
 * Both bars are `position: fixed` with a `backdrop-filter`, so content passes
 * under them as you scroll. At 0.92 nothing survived the trip: the filter had
 * been there all along with nothing to show for it. 0.62 is where a table row
 * sliding under the bar becomes a thing you can see without becoming a thing you
 * try to read.
 *
 * The base colours were re-cut for it rather than left alone; a translucent
 * surface is its base only in part, so dropping the alpha without lifting the
 * base is how the chrome quietly stops being a surface. Measured over the
 * canvas, the composite lands at 1.07:1 in both modes; at the old base and this
 * alpha, dark managed 1.04:1 and had all but dissolved.
 */
const CHROME_ALPHA = 0.62;

interface Ambient {
  /** Ready-to-use CSS `background` for the decorative layer. */
  layer: string;
  /**
   * The most saturated point any halo reaches, composited over the canvas.
   *
   * Not decoration's business, but the contract's: the chrome is translucent, so
   * the halo behind it changes what the nav's text actually sits on. This is the
   * worst case `palette.test.ts` measures against.
   */
  peak: string;
}

/**
 * The wash behind the chrome.
 *
 * Glass needs something to be glass over. A `backdrop-filter` above a flat fill
 * is an expensive way to draw the fill, which is what the shell did for as long
 * as it had one.
 *
 * This is not the decorative layer that was removed a few commits ago, and the
 * difference is the point. That one was a photo banner plus a logo watermark at
 * 55%: recognisable shapes, crossing the cards, competing with the data. This is
 * three flat radial gradients, fixed, behind everything, whose entire job is to
 * give the filter a gradient to bend. Nothing in it is legible, and nothing in it
 * lands on top of content.
 *
 * Light carries roughly half of dark's opacity. The same wash on a pale canvas
 * is not the same wash: dark absorbs colour, light broadcasts it.
 */
const AMBIENT: Record<PaletteMode, Ambient> = {
  dark: {
    layer: [
      'radial-gradient(58rem 44rem at -8% -12%, rgba(0,224,218,0.30), transparent 70%)',
      'radial-gradient(48rem 38rem at -4% 112%, rgba(255,0,140,0.18), transparent 70%)',
      'radial-gradient(54rem 40rem at 76% -14%, rgba(0,224,218,0.20), transparent 70%)',
    ].join(', '),
    peak: '#054C4F',
  },
  light: {
    layer: [
      'radial-gradient(58rem 44rem at -8% -12%, rgba(0,224,218,0.14), transparent 70%)',
      'radial-gradient(48rem 38rem at -4% 112%, rgba(255,0,140,0.09), transparent 70%)',
      'radial-gradient(54rem 40rem at 76% -14%, rgba(0,224,218,0.11), transparent 70%)',
    ].join(', '),
    peak: '#D2F3F6',
  },
};

/**
 * Dark takes the brand hues unchanged; light cannot take either of them.
 *
 * Measured on its own tint: the surface the active row actually puts it on:
 *
 *   #00E0DA   1.36:1      the brand teal, i.e. the client's active row was
 *                         not dim, it was gone
 *   #FFC200   1.62:1      the brand gold, same story in /admin
 *   #046663   5.59:1
 *   #7D5C00   5.05:1
 *
 * Neither brand value survives on a pale surface, and no opacity of the tint
 * rescues them; the ink is the problem. So light darkens both, exactly as the
 * data-viz scale does: the hue holds the identity, the luminance holds the
 * legibility, and each does one job.
 *
 * The tint recipe stays identical across modes on purpose. It is the brand hue
 * at low opacity over whatever the chrome is, so it inherits the mode rather
 * than restating it, and the active row stays recognisably teal or amber even
 * where the ink on it has had to move.
 */
const ACCENTS: Record<PaletteMode, Accents> = {
  dark: {
    client: { main: INVYO_TEAL, bg: 'rgba(0,224,218,0.10)', hover: 'rgba(0,224,218,0.15)' },
    admin: { main: INVYO_GOLD, bg: 'rgba(255,194,0,0.14)', hover: 'rgba(255,194,0,0.20)' },
  },
  light: {
    client: { main: '#046663', bg: 'rgba(0,224,218,0.10)', hover: 'rgba(0,224,218,0.16)' },
    admin: { main: '#7D5C00', bg: 'rgba(255,194,0,0.14)', hover: 'rgba(255,194,0,0.22)' },
  },
};

/** The four semantic severities, as MUI names them. */
export type Severity = 'success' | 'info' | 'warning' | 'error';

/**
 * Ink for a hue laid over its OWN low-opacity tint: the four severities, and
 * the brand magenta (`secondary`) which the list status chip wears for
 * `pending_review` (PI-108).
 */
export type SoftInk = Record<Severity | 'secondary', string>;

/**
 * What a `soft` surface writes with.
 *
 * The `soft` recipe (a hue at ~10% over the card, with the same hue as ink)
 * works in dark and collapses in light, and it collapses hardest on exactly the
 * colours a warning needs. Measured on the composite (the tint flattened over
 * the card, since the fill is translucent), light mode gives:
 *
 * | severity | `main` as ink | corrected |
 * | --- | --- | --- |
 * | success | 1.77:1 | **6.05:1** |
 * | info    | 1.55:1 | **6.37:1** |
 * | warning | 1.54:1 | **5.86:1** |
 * | error   | 2.92:1 | **5.99:1** |
 *
 * A 1.54:1 warning is not "a bit low", it is gold on cream; the message is
 * gone. Dark needs no correction at all (4.66:1 to 8.43:1), which is why the
 * problem survives an eye test done in the mode we develop in.
 *
 * Info and warning reuse `accent.client` / `accent.admin`: those ARE the brand
 * teal and gold, already darkened and already measured for this exact job. The
 * two new values are success and error, each darkened along its own hue:
 * #0B6B53 sits at 165° against `success.main`'s 164°, #A63239 at 356.4°, which
 * is `error.main`'s to the decimal. The hue carries the meaning; only the
 * luminance moved, which is the same trade `DATAVIZ_CATEGORICAL` makes above.
 *
 * Secondary came last (PI-108): the magenta was the one chip colour without a
 * soft entry, so a `soft` chip asked for it and got no tint at all. The brand
 * magenta holds in dark (4.88:1 on its own tint); in light it walks down its
 * own 327° to #B30062: 5.33:1, and still the pink it was.
 *
 * Ratios are asserted in `palette.test.ts`; the table above is a claim, that
 * file is the proof.
 */
const SOFT_INK: Record<PaletteMode, SoftInk> = {
  dark: {
    success: '#2DD4A8',
    info: INVYO_TEAL,
    warning: INVYO_GOLD,
    error: '#E8636B',
    secondary: INVYO_MAGENTA,
  },
  light: {
    success: '#0B6B53',
    info: ACCENTS.light.client.main,
    warning: ACCENTS.light.admin.main,
    error: '#A63239',
    secondary: '#B30062',
  },
};

/**
 * The spotlight, per mode: gold walked down until it can carry white.
 *
 * Gold because the figure it frames is a warning in slow motion: the ring fills
 * as the data ages, and a full ring means an enrichment now fires without
 * asking. `warning.main` is the arc drawn on top, so `bg` also has to clear 3:1
 * against it (WCAG 1.4.11: the arc is a graphical object, not text).
 *
 * Light reuses `accent.admin.main`: that value is already the brand gold
 * darkened and already measured for ink duty, so the spotlight is the same
 * colour the /admin chrome writes with rather than a fifth gold nobody can
 * trace. Dark can sit lower; the card has a near-black canvas under it.
 *
 * Both are dark fills. A spotlight is a block you look *at*, and in light mode
 * there is no room above white to make one; the difference has to point down.
 */
const SPOTLIGHT: Record<PaletteMode, Spotlight> = {
  dark: { bg: '#6B5210', ink: '#FFFFFF', muted: '#E0CDA4' },
  light: { bg: ACCENTS.light.admin.main, ink: '#FFFFFF', muted: '#EFE0BC' },
};

const CATEGORY: Record<PaletteMode, CategoryInks> = {
  //      gold 42°      violet 255°    blue 215°      pink 330°
  dark: { exit: '#F2C14E', ba: '#A78BFA', fo: '#5B9DF5', clevel: '#F472B6' },
  light: { exit: '#8A6100', ba: '#6440B8', fo: '#1D5DB5', clevel: '#AD2468' },
};

const CONTACT_INK: Record<PaletteMode, ContactInks> = {
  dark: { verified: '#66E03E', unverified: '#E8636B' },
  light: { verified: '#45962F', unverified: '#A63239' },
};

const FOCUS_RING: Record<PaletteMode, string> = {
  dark: '#00E0DA',
  light: '#039493',
};

/**
 * Build the palette for a mode.
 *
 * `contrastText` is declared on every swatch. Leaving it out is not neutral:
 * MUI then computes one silently, so nothing states our intent and no test can
 * hold it. (Ironically the swatches that had none were fine; the two that were
 * hand-written were the broken ones.)
 */
export default function buildPalette(mode: PaletteMode): PaletteOptions {
  const s = SURFACES[mode];
  const isDark = mode === 'dark';

  return {
    mode,
    primary: {
      main: INVYO_TEAL,
      light: '#33E8E3',
      dark: '#00B3AE',
      contrastText: INVYO_INK,
    },
    secondary: {
      main: INVYO_MAGENTA,
      light: '#FF3DA3',
      dark: '#CC006F',
      // Dark ink reads unusual on magenta, but white is 3.73:1, under AA for a
      // 13px/600 button label. A solid magenta button with white text would have
      // to use `secondary.dark` (#CC006F, 5.53:1).
      contrastText: INVYO_INK,
    },
    warning: {
      main: INVYO_GOLD,
      light: '#FFD333',
      dark: '#CC9B00',
      contrastText: INVYO_INK,
    },
    success: { main: '#2DD4A8', light: '#5EE0BF', dark: '#1FAF8A', contrastText: INVYO_INK },
    error: { main: '#E8636B', light: '#EE8A90', dark: '#C44D55', contrastText: INVYO_INK },
    info: { main: INVYO_TEAL, light: '#33E8E3', dark: '#00B3AE', contrastText: INVYO_INK },

    background: { default: s.default, paper: s.paper },
    surface: {
      card: s.card,
      // 0.24 is where a 1px line starts resolving on this canvas (1.47:1 against
      // the card, 1.41:1 against the default). The old 0.06 sat at 1.08:1 and
      // drew nothing at all; the card's edge came from the fill, not from it.
      cardBorder: alpha(s.neutral, isDark ? 0.24 : 0.3),
      elevated: s.elevated,
      elevatedBorder: alpha(s.neutral, isDark ? 0.3 : 0.34),
      chrome: alpha(s.chrome, CHROME_ALPHA),
      chromeBorder: alpha(s.neutral, 0.35),
      ambient: AMBIENT[mode],
    },
    /**
     * The data table's header band, and the only colour MUI X will not take
     * from a stylesheet.
     *
     * Every header cell paints its own background from this token (as do the
     * pinned headers, the trailing filler and the scrollbar filler), so a rule
     * on the header container lands underneath all of them. `headerBg` is the
     * one place that reaches every one of them at once.
     *
     * The header stays on the card and the DATA is what recedes: `bg` sinks the
     * grid's own root to the canvas, so the rows sit in a well cut into the card
     * rather than on top of it. The toolbar above the grid keeps the card, so
     * the top of the table reads as one block of chrome (count, columns,
     * export, then the column names) with the data below it.
     *
     * The step either way is `default` → `card`, 1.11:1: the pair this file
     * already cites as reading "perfectly well across a large field". Light
     * lands at 1.08:1 and shifts hue as well as luminance (#F4F6FA against pure
     * white), with the header's bottom rule carrying the rest.
     *
     * MUI's own default is a header *lighter* than the rows (paper lightened
     * 5%), which reads as a first row rather than as a heading.
     */
    DataGrid: { headerBg: s.card, bg: s.default },
    dataviz: { categorical: DATAVIZ_CATEGORICAL[mode], neutral: DATAVIZ_NEUTRAL[mode] },
    accent: ACCENTS[mode],
    softInk: SOFT_INK[mode],
    spotlight: SPOTLIGHT[mode],
    focusRing: FOCUS_RING[mode],
    category: CATEGORY[mode],
    contactInk: CONTACT_INK[mode],
    text: { primary: s.textPrimary, secondary: s.textSecondary, disabled: s.textDisabled },
    divider: alpha(s.neutral, isDark ? 0.1 : 0.18),

    // Only the shades actually consumed (StatusChip, the Metrics duration bars) are set, so
    // they follow the mode; MUI's ramp fills the rest.
    grey: {
      400: isDark ? '#9BA8BC' : '#A8B3C4',
      500: s.neutral,
    },

    action: {
      hover: alpha(s.neutral, isDark ? 0.06 : 0.04),
      selected: alpha(s.neutral, isDark ? 0.12 : 0.08),
      focus: alpha(s.neutral, 0.12),
      active: s.textSecondary,
      disabled: s.textDisabled,
      disabledBackground: alpha(s.neutral, 0.12),
      disabledOpacity: 0.5,
    },
  };
}
